import Axios from 'api/client';
import { ContactType } from 'constants/contacts';
import { PledgeCoveredFee, PledgeCoveredFeePolicy } from 'constants/fees';
import { InstallmentFrequency, PledgeType } from 'constants/pledges';
import { ContactEmail, ContactPhone, Pledge } from 'types/domain';
import { PaginatedData } from 'types/pagination';
import { FilterParams } from 'types/params';

interface CreatePledgeParams {
    contact_id?: number;
    contact?: {
        first_name?: string;
        last_name?: string;
        emails: ContactEmail[];
        phones: ContactPhone[];
        company_name?: string;
        type: ContactType;
    };
    campaign_id?: number;
    fund_id: number | null;
    amount: number;
    installment_frequency: InstallmentFrequency;
    first_installment_date?: string;
    fulfilled_at: string;
    number_of_installments: number;
    installments: {
        date: string;
        amount: number;
    }[];
    type: PledgeType;
    auto_remind: boolean;
    memo: string;
    internal_note: string;
    fee_coverage_type: PledgeCoveredFee;
    fee_coverage_visibility: PledgeCoveredFeePolicy;
    include_in_campaign_goal: boolean;
    allow_custom_amount?: boolean;
    notify_immediately: boolean;
}

interface EditPledgeParams {
    id: number;
    amount: number;
    contact_id?: number;
    campaign_id?: number;
    fund_id: number | null;
    fulfilled_at?: string;
    installment_frequency: InstallmentFrequency;
    installments?: {
        date: string;
        amount: number;
    }[];
    allow_custom_amount?: boolean;
    auto_remind?: boolean;
    type: PledgeType;
    memo: string;
    internal_note: string;
    fee_coverage_type: PledgeCoveredFee;
    fee_coverage_visibility: PledgeCoveredFeePolicy;
    include_in_campaign_goal: boolean;
}

interface AttachTransactionParams {
    transaction_id: number;
}

interface DetachTransactionParams {
    transaction_id: number;
}

interface SendEmailParams {
    email?: string;
    memo?: string;
    campaign_id?: number;
}

interface FetchPledgesParams extends FilterParams {}

const fetchPledges = (accountId: number, params: FetchPledgesParams) => {
    const { filters, ...restParams } = params;
    return Axios.post<PaginatedData<Pledge>>(`/accounts/${accountId}/get_pledges`, {
        ...filters,
        ...restParams,
    }).then(response => response.data);
};

const fetchPledge = (accountId: number, pledgeId: number) => {
    return Axios.get<Pledge>(`/accounts/${accountId}/pledges/${pledgeId}`).then(
        response => response.data,
    );
};

interface CalculateInstallmentsParams {
    total: number;
    frequency: InstallmentFrequency;
    start_date?: string;
    number_of_installments?: number;
    installments?: {
        override?: boolean;
        amount: number;
        date: string;
    }[];
}

interface CalculateInstallmentsResponse {
    total: number;
    end_date: string;
    installments: { date: string; amount: number }[];
}

interface PledgeStatsResponse {
    outstanding_pledge_payments: number;
    realized_pledge_payments: number;
    active_pledges_count: number;
}

const createPledge = (accountId: number, pledge: CreatePledgeParams) =>
    Axios.post<Pledge>(`/accounts/${accountId}/pledges`, pledge).then(response => response.data);

const calculateInstallments = (accountId: number, params: CalculateInstallmentsParams) =>
    Axios.post<CalculateInstallmentsResponse>(
        `/accounts/${accountId}/pledges/simulate`,
        params,
    ).then(response => response.data);
const deletePledge = (accountId: number, pledgeId: number) =>
    Axios.delete(`/accounts/${accountId}/pledges/${pledgeId}`);

const cancelPledge = (accountId: number, pledgeId: number) =>
    Axios.post(`/accounts/${accountId}/pledges/${pledgeId}/cancel`);

const editPledge = (accountId: number, pledge: EditPledgeParams) =>
    Axios.patch(`/accounts/${accountId}/pledges/${pledge.id}`, pledge);

const editPledgeInternalNote = (accountId: number, pledgeId: number, internal_note: string) =>
    Axios.patch(`/accounts/${accountId}/pledges/${pledgeId}/internal-note`, { internal_note });

const attachTransaction = (accountId: number, pledgeId: number, params: AttachTransactionParams) =>
    Axios.post(`/accounts/${accountId}/pledges/${pledgeId}/transactions/link`, params);

const detachTransaction = (accountId: number, pledgeId: number, params: DetachTransactionParams) =>
    Axios.post(`/accounts/${accountId}/pledges/${pledgeId}/transactions/unlink`, params);

const fetchPledgesStats = (accountId: number) =>
    Axios.get<PledgeStatsResponse>(`/accounts/${accountId}/pledges/stats`).then(
        response => response.data,
    );

const exportPledges = (accountId: number) =>
    Axios.post(`/accounts/${accountId}/exports`, {
        resource: 'pledges',
        object_id: accountId.toString(),
        object: 'account',
        report_type: 'csv',
    });

const sendEmail = (accountId: number, pledgeId: number, params: SendEmailParams) =>
    Axios.post(`accounts/${accountId}/pledges/${pledgeId}/send`, params);

export {
    fetchPledges,
    fetchPledge,
    createPledge,
    calculateInstallments,
    deletePledge,
    cancelPledge,
    editPledge,
    editPledgeInternalNote,
    exportPledges,
    fetchPledgesStats,
    attachTransaction,
    detachTransaction,
    sendEmail,
};

export type {
    CreatePledgeParams,
    CalculateInstallmentsParams,
    FetchPledgesParams,
    EditPledgeParams,
};
